import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { SxProps, Theme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { QueryBuilderMaterial } from "@react-querybuilder/material";
import {
  QueryBuilder,
  RuleComponents,
  RuleGroupBodyComponents,
  type ActionProps,
  type CombinatorSelectorProps,
  type Field,
  type FieldSelectorProps,
  type OperatorSelectorProps,
  type QueryValidator,
  type RuleGroupProps,
  type RuleGroupType,
  type RuleProps,
  type UseRuleGroup,
  type ValueEditorProps,
  useRule,
} from "react-querybuilder";
import { useNavigate } from "react-router-dom";
import "react-querybuilder/dist/query-builder.css";
import "./EnhancedTaggingRuleConditionBuilder.css";
import { buildAiPlanPath } from "../constants";
import { useConfigurableConcepts } from "../../../hooks/useConfigurableConcepts";
import { getConceptDisplayLabel } from "../../../services/autoTaggingService";
import ConceptValuesMultiSelect from "../TaggingPlan/ConceptValuesMultiSelect";
import {
  FIELD_PLACEHOLDER_LABEL,
  FIELD_PLACEHOLDER_NAME,
  createEmptyConditionValue,
  isFieldPlaceholderValue,
  parseEnhancedTaggingConditionValue,
  type EnhancedTaggingConditionValue,
} from "./enhancedTaggingConditionValue";
import {
  countEnhancedTaggingConditionRules,
  hasValidationReason,
  validateEnhancedTaggingConditionQuery,
} from "./validateEnhancedTaggingCondition";
import {
  COUNT_EMPTY_FIELD,
  COUNT_OPERATORS,
  COUNT_TARGET_PLACEHOLDER,
  DEFAULT_COUNT_OPERATOR,
  countNumberFromRuleValue,
  countOperatorLabel,
  countTargetFromField,
  getRuleAtPath,
  isCountField,
  normalizeCountNumber,
  replaceRuleAtPath,
  toCountRule,
  toDimensionRule,
} from "./countCondition";
import {
  fetchConditionalCountOptions,
  type ConditionalCountOption,
} from "./countOptions";

type ConditionBuilderContext = {
  enableCountConditions?: boolean;
  countTargets?: ConditionalCountOption[];
  countOptionsLoading?: boolean;
  countOptionsError?: string | null;
};

export type { EnhancedTaggingConditionValue };
export {
  createEmptyConditionValue,
  isFieldPlaceholderValue,
  parseEnhancedTaggingConditionValue,
};

/** Match Concept Values / Tagging Plan outlined selects; keep soft gray border. */
const conditionSelectFormSx: SxProps<Theme> = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: 40,
    borderRadius: "6px",
    bgcolor: "#fff",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
    borderWidth: 1,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

export const createEmptyConditionQuery = (): RuleGroupType => ({
  combinator: "or",
  rules: [
    {
      combinator: "and",
      rules: [
        {
          // Empty string (not FIELD_PLACEHOLDER_NAME) so RQB still shows
          // operator + value controls alongside the field selector.
          field: "",
          operator: "containsAny",
          value: createEmptyConditionValue(),
        },
      ],
    },
  ],
});

const OPERATORS = [
  { name: "containsAny", label: "containsAny" },
  { name: "containsAll", label: "containsAll" },
  { name: "doesNotContainAny", label: "doesNotContainAny" },
];

function CountNumberEditor({
  value,
  disabled,
  onChange,
}: {
  value: unknown;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  const [draft, setDraft] = useState(() =>
    String(countNumberFromRuleValue(value)),
  );

  useEffect(() => {
    setDraft(String(countNumberFromRuleValue(value)));
  }, [value]);

  const commit = (next: string) => {
    const normalized = normalizeCountNumber(next);
    setDraft(String(normalized));
    onChange(normalized);
  };

  const current = normalizeCountNumber(draft);

  return (
    <Box
      className="etr-count-number"
      sx={{
        display: "flex",
        width: "100%",
        height: 40,
        border: "1px solid #E5E7EB",
        borderRadius: "6px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <TextField
        value={draft}
        disabled={disabled}
        placeholder="0"
        onChange={(event) => {
          const cleaned = event.target.value.replace(/[^\d.]/g, "");
          const dot = cleaned.indexOf(".");
          setDraft(
            dot === -1
              ? cleaned
              : `${cleaned.slice(0, dot + 1)}${cleaned.slice(dot + 1).replace(/\./g, "")}`,
          );
        }}
        onBlur={() => commit(draft)}
        slotProps={{
          htmlInput: {
            "aria-label": "Count",
            inputMode: "numeric",
          },
        }}
        sx={{
          flex: 1,
          "& .MuiInputBase-root": {
            height: 40,
            fontSize: "0.875rem",
          },
          "& fieldset": { border: "none" },
        }}
      />
      <Box
        sx={{
          width: 28,
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #E5E7EB",
        }}
      >
        <IconButton
          size="small"
          aria-label="Increase count"
          disabled={disabled}
          onClick={() => commit(String(current + 1))}
          sx={{ flex: 1, borderRadius: 0, p: 0 }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Decrease count"
          disabled={disabled || current <= 0}
          onClick={() => commit(String(Math.max(0, current - 1)))}
          sx={{ flex: 1, borderRadius: 0, p: 0 }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function ConceptFieldSelector({
  className,
  handleOnChange,
  options,
  value,
  disabled,
  title,
  validation,
  schema,
  path,
  context,
}: FieldSelectorProps) {
  const builderContext = (context ?? {}) as ConditionBuilderContext;
  const countEnabled = Boolean(builderContext.enableCountConditions);
  const countMode = countEnabled && isCountField(String(value ?? ""));
  const flatOptions = useMemo(() => {
    const result: Array<{ name: string; label: string }> = [];
    for (const option of options) {
      if ("options" in option && Array.isArray(option.options)) {
        for (const nested of option.options) {
          const name = String(
            "name" in nested ? nested.name : (nested as { value?: string }).value ?? "",
          );
          const label = String(
            "label" in nested ? nested.label : name,
          );
          result.push({ name, label });
        }
      } else {
        const name = String(
          "name" in option ? option.name : (option as { value?: string }).value ?? "",
        );
        const label = String("label" in option ? option.label : name);
        result.push({ name, label });
      }
    }
    return result.filter((opt) => !isFieldPlaceholderValue(opt.name));
  }, [options]);

  const showError = hasValidationReason(validation, "field");
  const hideTargets =
    Boolean(builderContext.countOptionsLoading) ||
    Boolean(builderContext.countOptionsError);
  const countTargets = builderContext.countTargets ?? [];
  const selectedTarget = countTargetFromField(String(value ?? ""));
  const selectedOption = countTargets.find(
    (target) => target.field === selectedTarget?.field,
  );

  const switchType = (nextType: "count" | "dimension") => {
    const query = schema.getQuery() as RuleGroupType;
    const current = getRuleAtPath(query, path);
    if (!current) return;
    schema.dispatchQuery(
      replaceRuleAtPath(
        query,
        path,
        nextType === "count" ? toCountRule(current) : toDimensionRule(current),
      ),
    );
  };

  const conceptSelect = (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled}
      title={title}
      error={showError}
      sx={conditionSelectFormSx}
    >
      <Select
        displayEmpty
        error={showError}
        value={isFieldPlaceholderValue(value) ? "" : value}
        onChange={(event) => {
          handleOnChange(String(event.target.value ?? ""));
        }}
        renderValue={(selected) => {
          if (!selected || isFieldPlaceholderValue(selected)) {
            return (
              <Box component="span" sx={{ color: "#9ca3af" }}>
                {FIELD_PLACEHOLDER_LABEL}
              </Box>
            );
          }
          const matched = flatOptions.find((opt) => opt.name === selected);
          return matched?.label ?? selected;
        }}
      >
        <MenuItem value="">
          <Box component="span" sx={{ color: "#9ca3af" }}>
            {FIELD_PLACEHOLDER_LABEL}
          </Box>
        </MenuItem>
        {flatOptions.map((opt) => (
          <MenuItem key={opt.name} value={opt.name}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const targetSelect = (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled || hideTargets}
      error={showError}
      sx={conditionSelectFormSx}
    >
      <Select
        displayEmpty
        error={showError}
        value={isCountTargetFieldValue(String(value ?? "")) ? value : COUNT_EMPTY_FIELD}
        inputProps={{ "aria-label": "What to count" }}
        onChange={(event) => handleOnChange(String(event.target.value ?? ""))}
        renderValue={(selected) => {
          if (builderContext.countOptionsLoading) {
            return <Box component="span" sx={{ color: "#9ca3af" }}>Loading...</Box>;
          }
          const matched =
            countTargets.find((target) => target.field === selected) ??
            countTargetFromField(String(selected ?? ""));
          if (!matched) {
            return (
              <Box component="span" sx={{ color: "#9ca3af" }}>
                {COUNT_TARGET_PLACEHOLDER}
              </Box>
            );
          }
          return "name" in matched ? matched.name : matched.label;
        }}
      >
        <MenuItem value={COUNT_EMPTY_FIELD}>
          <Box component="span" sx={{ color: "#9ca3af" }}>
            {COUNT_TARGET_PLACEHOLDER}
          </Box>
        </MenuItem>
        {hideTargets
          ? selectedTarget
            ? (
              <MenuItem key={selectedTarget.field} value={selectedTarget.field}>
                {selectedOption?.name ?? selectedTarget.label}
              </MenuItem>
            )
            : null
          : countTargets.map((target) => (
              <MenuItem key={target.field} value={target.field}>
                {target.name}
              </MenuItem>
            ))}
        {!hideTargets &&
        selectedTarget &&
        !countTargets.some((target) => target.field === selectedTarget.field) ? (
          <MenuItem value={selectedTarget.field}>{selectedTarget.label}</MenuItem>
        ) : null}
      </Select>
    </FormControl>
  );

  return (
    <Box className={`${className ?? ""} etr-field-row`} title={title}>
      {countEnabled ? (
        <FormControl
          size="small"
          disabled={disabled}
          sx={{ ...conditionSelectFormSx, width: 120, flex: "none" }}
        >
          <Select
            value={countMode ? "count" : "dimension"}
            inputProps={{ "aria-label": "Condition type" }}
            onChange={(event) =>
              switchType(event.target.value === "count" ? "count" : "dimension")
            }
          >
            <MenuItem value="dimension">Dimension</MenuItem>
            <MenuItem value="count">Count</MenuItem>
          </Select>
        </FormControl>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {countMode ? targetSelect : conceptSelect}
      </Box>
    </Box>
  );
}

function isCountTargetFieldValue(field: string): boolean {
  return countTargetFromField(field) !== null;
}

function ConceptOperatorSelector({
  className,
  handleOnChange,
  options,
  value,
  disabled,
  title,
  validation,
}: OperatorSelectorProps) {
  const flatOptions = useMemo(() => {
    const result: Array<{ name: string; label: string }> = [];
    for (const option of options) {
      if ("options" in option && Array.isArray(option.options)) {
        for (const nested of option.options) {
          const name = String(
            "name" in nested ? nested.name : (nested as { value?: string }).value ?? "",
          );
          const label = String(
            "label" in nested ? nested.label : name,
          );
          result.push({ name, label });
        }
      } else {
        const name = String(
          "name" in option ? option.name : (option as { value?: string }).value ?? "",
        );
        const label = String("label" in option ? option.label : name);
        result.push({ name, label });
      }
    }
    return result;
  }, [options]);

  const showError = hasValidationReason(validation, "operator");

  return (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled}
      className={className}
      title={title}
      error={showError}
      sx={conditionSelectFormSx}
    >
      <Select
        displayEmpty
        error={showError}
        value={value ?? ""}
        onChange={(event) => handleOnChange(String(event.target.value ?? ""))}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{ color: "#9ca3af" }}>
                Please select...
              </Box>
            );
          }
          const matched = flatOptions.find((opt) => opt.name === selected);
          return matched?.label ?? selected;
        }}
      >
        {flatOptions.map((opt) => (
          <MenuItem key={opt.name} value={opt.name}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function IconTrashAction({
  handleOnClick,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <IconButton
      size="small"
      title={title}
      disabled={disabled}
      className={`etr-icon-action ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
      aria-label={title}
    >
      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
    </IconButton>
  );
}

function AddConditionAction({
  handleOnClick,
  label,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <Button
      type="button"
      disableRipple
      disabled={disabled}
      title={title}
      className={`etr-add-condition ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
    >
      {label}
    </Button>
  );
}

function AddGroupAction({
  handleOnClick,
  label,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <Button
      type="button"
      variant="outlined"
      fullWidth
      disabled={disabled}
      title={title}
      className={`etr-add-group ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
    >
      {label}
    </Button>
  );
}

function ConditionRule(props: RuleProps) {
  const rule = useRule(props);
  const ruleIndex = props.path[props.path.length - 1] ?? 0;
  const connectorLabel = ruleIndex === 0 ? "when" : "and";

  return (
    <div className={`${rule.outerClassName} etr-rule-vertical`}>
      <span className="etr-when-label">{connectorLabel}</span>
      <RuleComponents {...rule} />
    </div>
  );
}

function ConditionGroupHeader(props: RuleGroupProps & UseRuleGroup) {
  const {
    path,
    schema,
    disabled,
    removeGroup,
    classNames,
    translations,
  } = props;

  // Root: add-group lives below the body (see ConditionGroupBody)
  if (path.length === 0) {
    return null;
  }

  // Nested condition card header
  const groupIndex = (path[path.length - 1] ?? 0) + 1;
  const RemoveGroup = schema.controls.removeGroupAction;

  return (
    <>
      <span className="etr-group-title">Condition group {groupIndex}</span>
      {RemoveGroup ? (
        <RemoveGroup
          testID="remove-group"
          label={translations.removeGroup.label}
          title={translations.removeGroup.title}
          className={classNames.removeGroup}
          handleOnClick={removeGroup}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
    </>
  );
}

function ConditionGroupBody(props: RuleGroupProps & UseRuleGroup) {
  const {
    path,
    schema,
    disabled,
    addRule,
    addGroup,
    classNames,
    translations,
  } = props;
  const AddRule = schema.controls.addRuleAction;
  const AddGroup = schema.controls.addGroupAction;

  return (
    <>
      <RuleGroupBodyComponents {...props} />
      {path.length > 0 && AddRule ? (
        <AddRule
          testID="add-rule"
          label={translations.addRule.label}
          title={translations.addRule.title}
          className={classNames.addRule}
          handleOnClick={addRule}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
      {path.length === 0 && AddGroup ? (
        <AddGroup
          testID="add-group"
          label={translations.addGroup.label}
          title={translations.addGroup.title}
          className={classNames.addGroup}
          handleOnClick={addGroup}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
    </>
  );
}

function OrMeetsCombinator({
  className,
  ruleGroup,
}: CombinatorSelectorProps) {
  // Only show between root-level condition groups (OR). Nested AND groups stay implicit.
  if (!("combinator" in ruleGroup) || ruleGroup.combinator !== "or") {
    return null;
  }
  return <span className={`etr-or-meets ${className ?? ""}`}>Or meets</span>;
}

function ConceptValueEditor(props: ValueEditorProps) {
  const {
    field,
    fieldData,
    handleOnChange,
    value,
    disabled,
    operator,
    validation,
  } = props;

  if (isCountField(String(field ?? ""))) {
    return (
      <CountNumberEditor
        value={value}
        disabled={disabled}
        onChange={handleOnChange}
      />
    );
  }

  if (operator === "null" || operator === "notNull") {
    return null;
  }

  const parsed = parseEnhancedTaggingConditionValue(value);
  const conceptName =
    typeof fieldData?.label === "string" ? fieldData.label : undefined;
  const conceptCode =
    !field || isFieldPlaceholderValue(String(field)) ? "" : String(field);
  const showError = hasValidationReason(validation, "value");

  return (
    <Box className="etr-value-editor" sx={{ width: "100%", minWidth: 0 }}>
      <ConceptValuesMultiSelect
        hideLabel
        showAllOption={false}
        conceptCode={conceptCode}
        conceptName={conceptName}
        value={parsed.conceptValues}
        allValues={false}
        disabled={disabled || !conceptCode}
        error={showError}
        placeholder="Please select..."
        onChange={(next) => {
          handleOnChange({
            allValues: false,
            conceptValues: next.conceptValues,
          } satisfies EnhancedTaggingConditionValue);
        }}
      />
    </Box>
  );
}

type EnhancedTaggingRuleConditionBuilderProps = Readonly<{
  query: RuleGroupType;
  onQueryChange: (query: RuleGroupType) => void;
  /** API `target_type` used to load condition concept fields. */
  targetType: string;
  showValidation?: boolean;
  /** When true, omit the default Tagging Plan intro (parent supplies copy). */
  hideIntro?: boolean;
  /** Count conditions are only offered on Conditional Classification. */
  enableCountConditions?: boolean;
  onCountAvailabilityErrorChange?: (hasError: boolean) => void;
}>;

export default function EnhancedTaggingRuleConditionBuilder({
  query,
  onQueryChange,
  targetType,
  showValidation = false,
  hideIntro = false,
  enableCountConditions = false,
  onCountAvailabilityErrorChange,
}: EnhancedTaggingRuleConditionBuilderProps) {
  const navigate = useNavigate();
  const [countTargets, setCountTargets] = useState<ConditionalCountOption[]>(
    [],
  );
  const [countOperators, setCountOperators] = useState<string[]>([
    DEFAULT_COUNT_OPERATOR,
  ]);
  const [countOptionsLoading, setCountOptionsLoading] =
    useState(enableCountConditions);
  const [countOptionsError, setCountOptionsError] = useState<string | null>(
    null,
  );
  const [countOptionsAttempt, setCountOptionsAttempt] = useState(0);

  useEffect(() => {
    if (!enableCountConditions) {
      setCountOptionsLoading(false);
      setCountOptionsError(null);
      onCountAvailabilityErrorChange?.(false);
      return;
    }

    let cancelled = false;
    setCountOptionsLoading(true);
    setCountOptionsError(null);
    onCountAvailabilityErrorChange?.(false);
    fetchConditionalCountOptions()
      .then((next) => {
        if (cancelled) return;
        setCountTargets(next.targets);
        setCountOperators(
          next.operators.length > 0 ? next.operators : [DEFAULT_COUNT_OPERATOR],
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setCountTargets([]);
        setCountOperators([DEFAULT_COUNT_OPERATOR]);
        setCountOptionsError(
          error instanceof Error
            ? error.message
            : "Failed to load count options.",
        );
        onCountAvailabilityErrorChange?.(true);
      })
      .finally(() => {
        if (!cancelled) setCountOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [countOptionsAttempt, enableCountConditions, onCountAvailabilityErrorChange]);

  const countOperatorChoices = useMemo(
    () =>
      countOperators.map((operator) => ({
        name: operator,
        label: countOperatorLabel(operator),
      })),
    [countOperators],
  );

  const builderContext = useMemo<ConditionBuilderContext>(
    () => ({
      enableCountConditions,
      countTargets,
      countOptionsLoading,
      countOptionsError,
    }),
    [countOptionsError, countOptionsLoading, countTargets, enableCountConditions],
  );
  const { conceptOptions, loading: conceptsLoading } =
    useConfigurableConcepts(targetType);

  const fields = useMemo<Field[]>(
    () =>
      conceptOptions.map((opt) => ({
        name: opt.code,
        label: getConceptDisplayLabel(opt),
        valueEditorType: "select",
      })),
    [conceptOptions],
  );

  const ruleCount = useMemo(
    () => countEnhancedTaggingConditionRules(query),
    [query],
  );

  const validator = useCallback<QueryValidator>(
    (nextQuery) => {
      if (!showValidation) return true;
      const result = validateEnhancedTaggingConditionQuery(
        nextQuery as RuleGroupType,
      );
      if (result.ruleCount === 0) return false;
      return result.validationMap;
    },
    [showValidation],
  );

  const GuardedRemoveAction = useCallback(
    (props: ActionProps) => (
      <IconTrashAction
        {...props}
        disabled={props.disabled || ruleCount <= 1}
      />
    ),
    [ruleCount],
  );

  return (
    <Box className="etr-query-builder">
      {!hideIntro ? (
        <Typography sx={{ fontSize: "14px", color: "#595959", mb: 2, lineHeight: 1.5 }}>
          Configure concepts and values in the{" "}
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => navigate(buildAiPlanPath("taggingPlan"))}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Classification
          </Link>
          , then select them here to define Conditional Classification.
        </Typography>
      ) : null}
      {countOptionsError ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography role="alert" sx={{ fontSize: "12px", color: "#E7000B" }}>
            {countOptionsError}
          </Typography>
          <Button
            type="button"
            onClick={() => setCountOptionsAttempt((attempt) => attempt + 1)}
          >
            Retry
          </Button>
        </Box>
      ) : null}

      <QueryBuilderMaterial>
        <QueryBuilder
          fields={fields}
          query={query}
          onQueryChange={onQueryChange}
          context={builderContext}
          operators={[...OPERATORS, ...COUNT_OPERATORS, ...countOperatorChoices]}
          getOperators={(field) =>
            isCountField(String(field)) ? countOperatorChoices : OPERATORS
          }
          autoSelectOperator={false}
          disabled={conceptsLoading}
          showCombinatorsBetweenRules
          addRuleToNewGroups
          getDefaultValue={() => createEmptyConditionValue()}
          autoSelectField={false}
          validator={validator}
          combinators={[
            { name: "and", label: "And" },
            { name: "or", label: "Or meets" },
          ]}
          getRuleGroupClassname={(rg) =>
            rg.combinator === "or" ? "etr-root-group" : "etr-condition-group"
          }
          translations={{
            fields: {
              title: "Dimension",
              placeholderName: FIELD_PLACEHOLDER_NAME,
              placeholderLabel: FIELD_PLACEHOLDER_LABEL,
            },
            operators: { title: "Operator" },
            values: { title: "Value" },
            value: { title: "Value" },
            removeRule: { label: "Remove condition", title: "Remove condition" },
            removeGroup: { label: "Remove group", title: "Remove group" },
            addRule: { label: "+ Add condition", title: "Add condition" },
            addGroup: {
              label: "+ Add condition group",
              title: "Add condition group",
            },
          }}
          controlElements={{
            rule: ConditionRule,
            ruleGroupHeaderElements: ConditionGroupHeader,
            ruleGroupBodyElements: ConditionGroupBody,
            fieldSelector: ConceptFieldSelector,
            operatorSelector: ConceptOperatorSelector,
            valueEditor: ConceptValueEditor,
            combinatorSelector: OrMeetsCombinator,
            addRuleAction: AddConditionAction,
            addGroupAction: AddGroupAction,
            removeRuleAction: GuardedRemoveAction,
            removeGroupAction: GuardedRemoveAction,
            cloneRuleAction: null,
            cloneGroupAction: null,
            lockRuleAction: null,
            lockGroupAction: null,
            notToggle: null,
            dragHandle: null,
          }}
        />
      </QueryBuilderMaterial>
    </Box>
  );
}
